<script>
import { mapState, mapActions, mapGetters } from 'vuex';
import TransportationTableInputs from '../../components/transportation/TransportationTableInputs';
import TransportationCabinAdd from '../../components/transportation/TransportationCabinAdd';
import TransportationCabinEdit from '../../components/transportation/TransportationCabinEdit';
import ConfirmDialog from '../../components/TwConfirmDialog';
import transportTypes from '../../variables/transportTypes';

export default {
  name: 'TransportationCabins',
  components: {
    TransportationTableInputs,
    TransportationCabinAdd,
    TransportationCabinEdit,
    ConfirmDialog,
  },
  data() {
    return {
      headers: [
        { text: '#', sortable: false, width: '30px', class: 'px-2', value: '' },
        { text: 'Name', class: 'px-2', value: 'name' },
        { text: 'Website name', class: 'px-2', value: 'webName' },
        { text: 'Min Persons', class: "px-2", value: 'minPersons' },
        { text: 'Max Persons', class: 'px-2', value: 'maxPersons' },
        { text: 'External ref.', class: 'px-2', value: 'externalRef' },
        { text: 'Active', width: '70px', class: 'px-2', value: 'active' },
        { text: 'Actions', width: '70px', sortable: false, class: 'px-2', value: '' },
      ],
      pagination: {
        rowsPerPage: 20,
      },
      paginationRowsPerPage: [
        20,
        50,
        { text: 'All', value: -1 },
      ],
      addShow: false,
      editShow: false,
      changesMade: false,
      confirm: {
        title: 'Are you sure?',
        description: 'There are changes that are not saved',
      },
      ferry: transportTypes.FERRY,
    };
  },
  computed: {
    ...mapState({
      cabin: state => state.transportation.cabins.cabin,
      cabins: state => state.transportation.cabins.items,
      itemsError: state => state.transportation.cabins.itemsError,
      itemsLoading: state => state.transportation.cabins.itemsLoading,
      search: state => state.transportation.cabins.search,
      saveError: state => state.transportation.cabins.saveError,
      saveLoading: state => state.transportation.cabins.saveLoading,
      lastRoute: state => state.transportation.cabins.route,
      lastSupplier: state => state.transportation.cabins.supplier,
      routeId: state => state.transportation.cabins.route,
      routesLoading: state => state.transportation.routes.itemsLoading,
      supplier: state => state.transportation.suppliers.supplier,
      suppliers: state => state.transportation.suppliers.items,
      suppliersLoading: state => state.transportation.suppliers.itemsLoading,
    }),
    ...mapGetters({
      getRoutes: 'transportation/getRoutes',
    }),
    getSelectedRoute() {
      if (!this.routeId) {
        return null;
      }
      return this.getRoutes.find(route => route.id === this.routeId);
    },
  },
  methods: {
    ...mapActions({
      routesLoad: 'transportation/routesLoad',
      setCabinsSearch: 'transportation/setCabinsSearch',
      setActiveCabin: 'transportation/setActiveCabin',
      setCabinRoute: 'transportation/setCabinRoute',
    }),
    loadRoutes(force = false) {
      if (force || (this.supplier && this.supplier !== this.lastSupplier)) {
        this.routesLoad().then(() => {
          if (this.getRoutes.length) {
            this.setCabinRoute(this.getRoutes[0].id);
          }
        });
      }
    },
    openAddCabin() {
      this.addShow = true;
    },
    closeAddConfirm(force = false) {
      if (!force) {
        this.$refs.addConfirm.open();
      } else {
        this.addShow = false;
      }
    },
    openEditCabin(cabin) {
      if (!this.cabin || this.cabin.id !== cabin.id) {
        this.setActiveCabin(cabin);
      }
      this.editShow = true;
    },
    closeEditConfirm(force = false) {
      if (this.changesMade && !force) {
        this.$refs.confirm.open();
      } else {
        this.editShow = false;
      }
    },
  },
  created() {
    this.loadRoutes();
  },
};
</script>

<template>
  <v-container fluid>
    <v-layout>
      <v-flex xs6>
        <transportation-table-inputs
          @change="loadRoutes"
          @search="setCabinsSearch"
          :disabled="itemsLoading"
          :search="search"
        />
      </v-flex>
      <v-flex class="text-xs-right" xs3 offset-xs3>
        <v-btn
          @click="openAddCabin"
          :disabled="!supplier || !routeId"
          color="primary"
        >
          Add new cabin
        </v-btn>
      </v-flex>
    </v-layout>
    <v-layout>
      <v-flex xs3>
        <v-autocomplete
          @change="setCabinRoute"
          :items="getRoutes"
          :loading="suppliersLoading || routesLoading"
          :disabled="suppliersLoading || routesLoading"
          :value="routeId"
          label="Routes"
          item-text="name"
          item-value="id"
        />
      </v-flex>
      <v-flex v-if="getSelectedRoute" class="text-xs-center" xs1>
        <v-icon v-if="getSelectedRoute.type === ferry" title="ferry">directions_boat</v-icon>
        <v-icon v-else title="train">train</v-icon>
      </v-flex>
    </v-layout>
    <v-layout mt-3 row>
      <v-flex>
        <v-data-table
          :items="cabins"
          :headers="headers"
          :loading="itemsLoading"
          :pagination.sync="pagination"
          :rows-per-page-items="paginationRowsPerPage"
          :search="search"
          no-results-text="No results found"
          no-data-text="No cabins were found for the supplier and route"
          must-sort
        >
          <template slot="items" slot-scope="props">
            <tr
              :class="{ 'blue lighten-5': cabin && props.item.id === cabin.id }"
            >
              <td class="px-2 pr-4">{{ props.item.id }}</td>
              <td class="px-2">{{ props.item.name }}</td>
              <td class="px-2">{{ props.item.webName }}</td>
              <td class="px-2">{{ props.item.minPersons }}</td>
              <td class="px-2">{{ props.item.maxPersons }}</td>
              <td class="px-2">{{ props.item.externalRef }}</td>
              <td class="px-2">
                <v-icon v-if="props.item.active" color="green">done</v-icon>
                <v-icon v-else color="red">close</v-icon>
              </td>
              <td class="px-2">
                <v-icon @click="openEditCabin(props.item)" class="mr-2" small>
                  edit
                </v-icon>
              </td>
            </tr>
          </template>
          <template slot="no-data">
            <div v-if="itemsError">
              <v-alert :value="true" icon="warning">
                An error occurred while loading.
                <span class="refresh-text">
                  Try again?
                </span>
              </v-alert>
            </div>
            <div v-else-if="!supplier" class="text-xs-center">
              <span>Choose a supplier to load routes</span>
            </div>
            <div v-else-if="!itemsLoading" class="text-xs-center">
              <span>No cabins were found for the supplier and route</span>
            </div>
            <div class="text-xs-center" v-else>
              <!--Show nothing when loading-->
            </div>
          </template>
        </v-data-table>
      </v-flex>
      <v-dialog
        v-model="editShow"
        @keydown.esc="editShow = false"
        max-width="600px"
        no-click-animation
        persistent
      >
        <transportation-cabin-edit
          v-if="getSelectedRoute"
          v-model="changesMade"
          @close="closeEditConfirm"
          :shown="editShow"
          :type="getSelectedRoute.type"
        />
      </v-dialog>

      <v-dialog
        v-model="addShow"
        @keydown.esc="addShow = false"
        max-width="600px"
        no-click-animation
        persistent
      >
        <transportation-cabin-add
          v-if="getSelectedRoute"
          @close="closeAddConfirm"
          :shown="addShow"
          :type="getSelectedRoute.type"
        />
      </v-dialog>

      <confirm-dialog
        @confirm="addShow = false"
        title="Cancel add cabin"
        description="Are you sure you want to cancel adding a new cabin?"
        ref="addConfirm"
      />

      <confirm-dialog
        @confirm="editShow = false"
        :title="confirm.title"
        :description="confirm.description"
        ref="confirm"
      />
    </v-layout>
  </v-container>
</template>

<style type="scss">
  .v-messages {
    min-height: 14px !important;
  }
</style>



// WEBPACK FOOTER //
// src/pages/transportation/TransportationCabins.vue